import { measureListAnimationRow } from "./measureListAnimationRow";
import { storeInitialAndFinalRowPositionsLegacy } from "./storeInitialAndFinalRowPositionsLegacy";

export const storeInitialAndFinalRowPositions = (rows: Element[], animationRowIds: Set<string>, prevChildrenPositions: Map<string, DOMRect>, newChildrenPositions: Map<string, DOMRect>, rowItemParts: Map<string, Element[]>, isRemovalAction: boolean, useBatchedRowMeasurements: boolean = false): void => {
    if (!useBatchedRowMeasurements) {
        storeInitialAndFinalRowPositionsLegacy(rows, animationRowIds, prevChildrenPositions, newChildrenPositions, rowItemParts, isRemovalAction);
        return;
    }
    
    // Capture every initial position before insertion styles can invalidate layout.
    const measurements = rows.map(row => {
        const position = measureListAnimationRow(row, "storeInitialAndFinalRowPositions_rowBoundingClientRect");
        const itemPartWrapper = document.getElementById(`${row.id}_itemPartWrapper`);
        const itemParts = Array.from(itemPartWrapper?.children ?? []);
        const expansionContainer = itemParts.length > 0
            ? document.getElementById(`${row.id}_expansionAnimationWrapper`)
            : null;
        return {
            row,
            position,
            insertionHeight: position.height,
            itemParts: itemParts.map(itemPart => ({
                row: itemPart,
                position: measureListAnimationRow(itemPart, "storeInitialAndFinalRowPositions_itemPartDomRect"),
            })),
            expansion: expansionContainer
                ? {
                    row: expansionContainer,
                    position: measureListAnimationRow(expansionContainer, "storeInitialAndFinalRowPositions_expansionContainerDomRect"),
                }
                : undefined,
        };
    });
    
    if (!isRemovalAction) {
        const insertedRows = measurements.filter(({ row }) => animationRowIds.has(row.id));
        for (const { row } of insertedRows) {
            (row as HTMLDivElement).style.display = "block";
        }
        try {
            // Hidden insertion rows need a second measurement, with all display writes batched.
            for (const measurement of insertedRows) {
                measurement.insertionHeight = measureListAnimationRow(measurement.row, "storeInitialAndFinalRowPositions_netHeightChange_add").height;
            }
        }
        finally {
            for (const { row } of insertedRows) {
                (row as HTMLDivElement).style.display = "unset";
            }
        }
    }
    
    let netHeightChange = 0;
    for (const { row, position, insertionHeight, itemParts, expansion } of measurements) {
        prevChildrenPositions.set(row.id, position);
        newChildrenPositions.set(row.id, new DOMRect(position.x, position.y + netHeightChange, position.width, position.height));
        
        if (animationRowIds.has(row.id)) {
            netHeightChange += isRemovalAction ? -position.height : insertionHeight;
        }
        
        if (itemParts.length > 0) {
            // Shift an expanded conversation as a unit when its header has moved.
            if (netHeightChange !== 0) {
                if (expansion) {
                    const { row: expansionContainer, position: expansionPosition } = expansion;
                    prevChildrenPositions.set(expansionContainer.id, expansionPosition);
                    newChildrenPositions.set(expansionContainer.id, new DOMRect(expansionPosition.x, expansionPosition.y + netHeightChange, expansionPosition.width, expansionPosition.height));
                    
                    // Removing a header also removes its expansion.
                    if (animationRowIds.has(row.id) && isRemovalAction) {
                        netHeightChange -= expansionPosition.height;
                    }
                    rowItemParts.set(row.id, [expansionContainer]);
                }
            }
            else {
                let itemPartNetHeightChange = 0;
                for (const { row: itemPartRow, position: itemPartPosition } of itemParts) {
                    prevChildrenPositions.set(itemPartRow.id, itemPartPosition);
                    newChildrenPositions.set(itemPartRow.id, new DOMRect(itemPartPosition.x, itemPartPosition.y + itemPartNetHeightChange, itemPartPosition.width, itemPartPosition.height));
                    
                    if (animationRowIds.has(itemPartRow.id)) {
                        const itemPartHeight = itemPartPosition.height;
                        if (isRemovalAction) {
                            itemPartNetHeightChange -= itemPartHeight;
                            netHeightChange -= itemPartHeight;
                            // The header disappears with its last item part.
                            if (itemParts.length === 1) {
                                netHeightChange -= position.height;
                            }
                        }
                        else {
                            itemPartNetHeightChange += itemPartHeight;
                            netHeightChange += itemPartHeight;
                            (itemPartRow as HTMLDivElement).style.display = "unset";
                        }
                    }
                }
                rowItemParts.set(row.id, itemParts.map(itemPart => itemPart.row));
            }
        }
    }
};
